import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/serge'
import { getSergeProjectContext, saveServeClarificationAnswer, completeSergeClarification } from '@/services/sergeApi'
import { Loader2, Check } from 'lucide-react'

export default function SergeClarificationPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [questions, setQuestions] = useState<Question[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (projectId) {
      loadClarification()
    }
  }, [projectId])

  const loadClarification = async () => {
    try {
      setIsLoading(true)
      const context = await getSergeProjectContext(Number(projectId))
      const clarificationQuestions = context.context.clarification.questions || []
      setQuestions(clarificationQuestions)
      setProgress(context.context.clarification.progress || 0)

      // Charger les reponses existantes
      const existingAnswers: Record<string, string> = {}
      clarificationQuestions.forEach((q) => {
        if (q.answer) {
          existingAnswers[q.id] = q.answer
        }
      })
      setAnswers(existingAnswers)
    } catch (error) {
      toast.error('Erreur lors du chargement des questions')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleSaveAnswer = async (questionId: string) => {
    const answer = answers[questionId]
    if (!answer?.trim()) {
      toast.error('Veuillez saisir une reponse')
      return
    }

    try {
      setSavingId(questionId)
      const result = await sergeApi.saveAnswer(Number(projectId), questionId, answer)
      setProgress(result.progress)

      // Mettre a jour le statut de la question localement
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === questionId ? { ...q, status: 'answered' as const, answer } : q
        )
      )

      toast.success('Reponse enregistree')

      if (result.allAnswered) {
        toast.success('Toutes les questions ont ete repondues!')
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement')
    } finally {
      setSavingId(null)
    }
  }

  const handleComplete = async () => {
    try {
      await sergeApi.completeClarification(Number(projectId))
      toast.success('Etape de clarification terminee')
      navigate(`/projects/${projectId}/risks`)
    } catch (error) {
      toast.error('Erreur lors de la validation')
    }
  }

  const answeredCount = questions.filter((q) => q.status === 'answered').length
  const allAnswered = answeredCount === questions.length && questions.length > 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (questions.length === 0) {
    return (
      <div className="border border-border bg-surface p-8 text-center">
        <h2 className="text-lg font-semibold text-foreground">Aucune question</h2>
        <p className="text-sm text-foreground-secondary mt-2">
          L'analyse n'a pas genere de questions de clarification.
          Vous pouvez passer directement a l'etape suivante.
        </p>
        <Button
          variant="primary"
          size="md"
          className="mt-4"
          onClick={() => navigate(`/projects/${projectId}/risks`)}
        >
          Passer aux risques
        </Button>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      <section className="col-span-12 xl:col-span-8 space-y-4">
        {questions.map((question) => (
          <div key={question.id} className="border border-border bg-surface p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-primary">{question.priority}</span>
              <span
                className={`text-xs font-mono ${
                  question.status === 'answered' ? 'text-green-500' : 'text-foreground-muted'
                }`}
              >
                {question.status === 'answered' ? (
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3" /> Repondu
                  </span>
                ) : (
                  'En attente'
                )}
              </span>
            </div>
            <h2 className="text-lg font-semibold text-foreground mt-3">{question.title}</h2>
            <p className="text-sm text-foreground-secondary mt-2">{question.detail}</p>
            {question.category && (
              <span className="inline-block mt-2 text-xs font-mono text-foreground-muted bg-bg px-2 py-1 border border-border">
                {question.category}
              </span>
            )}
            <textarea
              rows={4}
              placeholder="Saisir la reponse..."
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className="mt-4 w-full border border-border bg-bg p-3 text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-primary"
            />
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSaveAnswer(question.id)}
                disabled={savingId === question.id}
              >
                {savingId === question.id ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </Button>
            </div>
          </div>
        ))}
      </section>

      <section className="col-span-12 xl:col-span-4 space-y-4">
        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Progression
          </h3>
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-foreground-secondary">
                {answeredCount} / {questions.length} questions
              </span>
              <span className="text-primary font-mono">{progress}%</span>
            </div>
            <div className="w-full bg-bg border border-border h-2">
              <div
                className="bg-primary h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          {allAnswered && (
            <Button variant="primary" size="md" className="w-full mt-4" onClick={handleComplete}>
              Valider et continuer
            </Button>
          )}
        </div>

        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Categories
          </h3>
          <ul className="mt-3 space-y-2">
            {Array.from(new Set(questions.map((q) => q.category).filter(Boolean))).map((cat) => (
              <li key={cat} className="text-sm text-foreground-secondary border border-border bg-bg p-2">
                {cat}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  )
}
